/* eslint-env browser */
import _ from 'lodash';

import React from 'react';
import { connect } from 'react-redux';
import { Flex, Box } from 'reflexbox';
import {
  Button,
  ButtonCircle,
  Checkbox,
  Text,
} from 'rebass';
import Loader from 'react-loading';

import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';

import { isSmartIterations } from 'shared/simcOptions';
import { MAX_LEVEL } from 'shared/character';

import config from 'frontend/config';
import {
  ErrorText,
  Icon,
  If,
  TinyButton,
} from 'frontend/components';

import actions from 'frontend/state/actions';
import { selectors as simbotSelectors } from 'frontend/state/reducers/simbot';
import { selectors as formSelectors } from 'frontend/state/reducers/simbot/form';
import { selectors as characterSelectors } from 'frontend/state/reducers/simbot/character';
import { selectors as userSelectors } from 'frontend/state/reducers/user';
import { selectors as simcOptionsSelectors } from 'frontend/state/reducers/simbot/simcOptions';
import { selectors as simcItemsSelectors } from 'frontend/state/reducers/simbot/simcItems';
import { selectors as optimizeSelectors } from 'frontend/state/reducers/simbot/optimize';
import { selectors as simcExpertSelectors } from 'frontend/state/reducers/simbot/simcExpertOptions';

const styleStripes = (color1, color2) => (
  `repeating-linear-gradient(-45deg, ${color1}, ${color1} 10px, ${color2} 10px, ${color2} 20px)`
);

const errors = {
  not_ready: 'Enter character realm/name or SimulationCraft script',
  invalid_commands: 'SimulationCraft Script contains commands that are invalid or not supported by Simbot at this time',
  too_many_cooks: 'Script can only contain one actor. Multiple characters or copy commands are not currently allowed',
  no_cooks: 'No class found, double check to make sure you didn\'t lose a line when copy/pastaing from /simc',
  char_not_found: 'Armory lookup problem, check for errors in Load From Armory section',
  healer: 'Healing specs are not supported by SimulationCraft at this time',
  unsupported_spec: 'Your spec is not supported by Raidbots and/or SimulationCraft at this time',
  no_spec: 'Character does not have a specialization',
  no_items: 'You must specify at least one item for Gear Compare',
  invalid_iterations: 'Number of iterations chosen is invalid',
  non_public_iterations: 'The number of iterations chosen is not supported in the Raidbots public queue',
  invalid_fight_style: 'Fight Style value provided is not supported',
  non_public_fight_style: 'The Fight Style you have selected is not supported in the Raidbots public queue',
  invalid_fight_length: 'Fight Length value provided is not supported',
  non_public_fight_length: 'The Fight Length you have selected is not supported in the Raidbots public queue',
  invalid_simc_version: 'SimC Version provided is not supported',
  non_public_simc_version: 'The SimC Version you have selected is not supported in the Raidbots public queue',
  invalid_advanced_commands: (
    'Advanced SimulationCraft Input or SimC Expert Mode contain options that are not allowed on Raidbots.'
  ),
  invalid_enemy_count: 'Enemy Count provided is not supported',
  invalid_enemy_type: 'Enemy Type provided is not supported',
  invalid_food: 'Food provided is not supported',
  invalid_augmentation: 'Augmentation provided is not supported',
  no_advanced_input: 'Must enter some input',
  missing_gear: 'SimC input has empty gear slots',
  invalid_talent_set_name: 'A talent set name is invalid. Make sure you are not using commas or double quotes',
  empty_gearset: 'One of your gearsets is empty or has an empty item in it',
  ilevel_too_high: 'An item in your gearsets has an ilevel that is too high',
  over_iterations_limit: 'Over iterations limit, deselect some gear, enhancements, or talents',
  only_one_combo: (
    'Top Gear requires at least two combinations. ' +
    'Make sure to select some additional items, enhancements, or talents'
  ),
  no_actors: 'No class/character found in input. Check for extraneous characters at the start of the text',
  invalid_apl: 'Custom APL has invalid commands. Only "actions" commands are allowed',
  report_name_length: 'Report name is too long (100 characters max)',
  'Network Error': 'A network error occurred. Check your connectivity',
  network_error: 'A network error occurred. Check your connectivity',
  internal_error: 'Seri\'s code did something bad that he did not expect (internal server error)',
  retry: 'Unable to start sim, please try again',
  site_unavailable: (
    'Raidbots is currently unavailable. Try again later'
  ),
  maintenance: 'Raidbots is down for maintenance',
  queue_maintenance: 'Raidbots queue is down for maintenance',
  queue_down_high_load: (
    'Raidbots queue is currently unavailable due to high traffic or server problems'
  ),
  simc_invalid_input: 'SimC input is invalid, details above',
  armory_no_weapon: 'No weapon found on your character, unable to sim',
  rate_limited: 'You have been temporarily rate limited. Try again in one minute',
  advanced_iteration_limit: 'Simulation is too large (too many iterations)',
  advanced_actor_limit: 'Simulation is too large (too many copies)',
  unknown_error: 'Seri\'s code did something unexpected that\'s not handled well',
  no_mix_armory_and_profilesets: 'You cannot mix armory lookups and profilesets in an advanced sim',
  must_use_smart: 'Very large sims must use Smart Sim, change Iterations under Simulation Options',
  custom_iterations_too_high: 'Custom iterations= is too large, please set to 100,000 or lower',
  custom_target_error_too_low: 'Custom target_error= is too low, please set to 0.05 or higher',
  fishing_pole_equipped: 'Your character has gone fishin\' (Fishing poles cannot be simulated)',
  invalid_weapon_equipped: 'Your character has an invalid weapon equipped',
  profileset_convert_failure: 'Convert to Profileset error',
  too_many_combos: 'Too many combinations, select fewer options',
  invalid_advanced_fight_style: (
    'The fight style selected in "Simulation Options" is not supported in advanced mode. Try switching to Patchwerk.'
  ),
  submit_new_tab_blocked: 'You need to allow pop-ups for middle-click or control-click to work on the submit button',
  droptimizer_low_level: `Droptimizer only works for level ${MAX_LEVEL} characters`,
  droptimizer_no_instance_selected: 'Please select a zone/raid',
  droptimizer_dungeons_not_premium: 'You must be a Raidbots Premium member to sim All Dungeons',
  invalid_weapon: 'Unusable weapon equipped. Make sure equipped weapons are valid for your spec',
  droptimizer_no_actors: 'Something weird happened, try clearing and re-entering your SimC input',
  droptimizer_same_alternates: 'Main hand and off hand alternates cannot be the same item',
  beta_sims_disabled: 'Sims are currently disabled on the beta site',
  over_max_level: 'Out of date character - log into WoW to refresh',
  out_of_date: 'Out of date character - log into WoW to refresh',
  forbidden: 'Forbidden - try reloading the site or logging out and logging back in',
  request_timeout: 'Request timeout - you may be having connectivity issues',
  too_many_concurrent_sims: 'You are running too many sims at once. Wait for some to finish.',
  old_addon_version: 'Your SimC addon is too old. Please install the latest version.',
  old_talents: 'Your /simc input is outdated (old talent format). Update your addon and run /simc again.',
  too_many_talent_points: 'Invalid talents - more points spent than possible at your level.',
  too_many_enhancements: 'Too many enhancements chosen. Reduce gem/enchant choices.',

  bad_spec_temporary_enchant_dual_wield: (
    'Warning! Simulation Options > Weapon Rune is intended for a dual wield spec and may not sim correctly'
    + ' for your character.'
  ),
  dungeon_slice_all_raid_buffs: (
    `All raid buffs are selected and you have Dungeon Slice chosen -
    if you are trying to sim dungeons the sim likely has too many buffs`
  ),
  spec_does_not_support_fight_style: (
    <span>
      Your spec does not support this fight style. Change the fight style under Simulation Options.
      {' '}
      <a
        href="https://support.raidbots.com/article/43-unreliable-fight-styles"
        target="_blank"
        rel="noopener noreferrer"
      >
        More Info
      </a>
    </span>
  ),

  unknown_sim_submit_error: (
    <span>
      Communication error - check the
      {' '}
      <a
        href="https://support.raidbots.com/article/46-general-troubleshooting"
        target="_blank"
        rel="noopener noreferrer"
      >
        General Troubleshooting
      </a>
      {' '}
      support article
    </span>
  ),
};

const buttonText = {
  gear: 'Compare Items',
  optimize: 'Find Top Gear',
  droptimizer: 'Run Droptimizer',
  stats: 'Generate Stat Weights',
  talents: 'Compare Talents',
  quick: 'Run Quick Sim',
  advanced: 'Run Simulation',
};

// This is a terrible hack - global state for error tracking/reporting is a great shame
let shouldReportError = true;

const SubmitSimulationButton = ({
  type,
  iterations,
  smartHighPrecision,
  loading,
  characterLoading,
  itemsLoading,
  optimizeLoading,
  ptr,
  error,
  isExpertActive,
  inputInCustomAplWarning,
  enableRuneWords,
  enableDominationShards,
  simOptionWarnings,

  onSubmit,
  onTogglePrecision,
  onClickClearApl,
}) => {
  if (error && !errors[error.type] && shouldReportError) {
    Sentry.captureMessage(`Unexpected response from server: ${error.type}`);
  }
  let somethingIsLoading = false;
  if (type === 'optimize') {
    somethingIsLoading = loading || optimizeLoading;
  } else {
    somethingIsLoading = loading || characterLoading || itemsLoading;
  }

  let warning;
  let warningSubtext;
  let warningColor = config.colors.orange;

  if (inputInCustomAplWarning) {
    warning = 'Addon input detected in "Custom APL and SimC Options"';
    warningSubtext = (
      <>
        Addon input from /simc is only allowed in the &quot;Load from SimC Addon&quot; section at the top.
        <TinyButton onClick={onClickClearApl} ml={1} theme="warning">
          Clear Custom APL
        </TinyButton>
      </>
    );
    warningColor = config.colors.error;
  }

  if (!warning && simOptionWarnings.length) {
    warning = errors[_.first(simOptionWarnings)];
  }

  return (
    <Box
      mt={2}
      style={{
        position: 'sticky',
        height: 85,
        bottom: '0px',
        padding: '10px 0 10px 0',
        background: config.colors.gray,
        zIndex: 10,
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
      }}
    >
      <Flex align="center" wrap>
        <Box mr={2}>
          <Button
            onClick={onSubmit}
            // prevent middle click on button from triggeting scroll mode
            onMouseDown={e => {
              e.preventDefault();
              if (document.activeElement && document.activeElement.blur) {
                // force a blur, the mousedown stuff doesn't seem to natively trigger blurs in the browser
                document.activeElement.blur();
              }
            }}
            // on middle click mouse up, trigger form submit
            onMouseUp={e => {
              const isMiddleClick = e.button === 1;
              const isControlClick = e.ctrlKey;

              if (isMiddleClick || isControlClick) {
                onSubmit(e);
              }
            }}
            backgroundColor={
              somethingIsLoading ? config.colors.primary : config.colors.green
            }
            color={config.colors.black}
            disabled={somethingIsLoading}
          >
            <Flex align="center">
              <Box>
                {buttonText[type]}
              </Box>
            </Flex>
          </Button>
        </Box>
        {loading ? (
          <Box mr={2}>
            <Loader
              type="spin"
              color={config.colors.green}
              height={20}
              width={20}
              delay={100}
            />
          </Box>
        ) : null}
        <If
          condition={ptr}
          then={() => (
            <Flex align="center" my={1} mr={2} style={{ lineHeight: 1 }}>
              <Box>
                <ButtonCircle
                  size={20}
                  color={config.colors.black}
                  backgroundColor={config.colors.warning}
                  style={{
                    minHeight: '20px',
                  }}
                  mr={1}
                >
                  <Icon name="warning" size={14} />
                </ButtonCircle>
              </Box>
              <Text size={14} color={config.colors.warning} bold>
                PTR Simulation
              </Text>
            </Flex>
          )}
        />
        <If
          condition={enableRuneWords || enableDominationShards}
          then={() => (
            <Flex align="center" my={1} mr={2} style={{ lineHeight: 1 }}>
              <Box>
                <ButtonCircle
                  size={20}
                  color={config.colors.black}
                  backgroundColor={config.colors.warning}
                  style={{
                    minHeight: '20px',
                  }}
                  mr={1}
                >
                  <Icon name="warning" size={14} />
                </ButtonCircle>
              </Box>
              <Text size={14} color={config.colors.warning} bold>
                Domination Shards are enabled in the sim but are not active in 9.2 content. Check Simulation Options
                section.
              </Text>
            </Flex>
          )}
        />
        <If
          condition={isExpertActive && type !== 'advanced'}
          then={() => (
            <Flex
              align="center"
              p={1}
              my={1}
              mr={2}
              style={{ lineHeight: 1, background: styleStripes('#292929', '#493900') }}
            >
              <Box>
                <ButtonCircle
                  size={20}
                  color={config.colors.black}
                  backgroundColor={config.colors.warning}
                  style={{
                    minHeight: '20px',
                  }}
                  mr={1}
                >
                  <Icon name="warning" size={14} />
                </ButtonCircle>
              </Box>
              <Text size={14} color={config.colors.white} bold>
                SimC Expert Mode is active
              </Text>
            </Flex>
          )}
        />
        <If
          condition={warning}
          then={() => (
            <Flex align="center" my={0}>
              <Box>
                <Text color={warningColor} size={14} bold>
                  {warning}
                </Text>
                <Text small>
                  {warningSubtext}
                </Text>
              </Box>
            </Flex>
          )}
        />
        {error && (
          <ErrorText pl={2}>
            {errors[error.type] ? errors[error.type] : 'Error that Seri forgot to handle'}
          </ErrorText>
        )}
      </Flex>
      <If
        condition={isSmartIterations(iterations)}
        then={() => (
          <Checkbox
            mt={1}
            id="smartHighPrecision"
            name="smartHighPrecision"
            label="High Precision (2x more precise, 4x slower)"
            checked={smartHighPrecision}
            onChange={onTogglePrecision}
            backgroundColor={config.colors.black}
          />
        )}
      />
    </Box>
  );
};

SubmitSimulationButton.propTypes = {
  type: PropTypes.string.isRequired,
  iterations: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  smartHighPrecision: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  characterLoading: PropTypes.bool,
  itemsLoading: PropTypes.bool,
  optimizeLoading: PropTypes.bool,
  error: PropTypes.shape({
    type: PropTypes.string,
    details: PropTypes.object,
  }),
  ptr: PropTypes.bool,
  isExpertActive: PropTypes.bool,
  inputInCustomAplWarning: PropTypes.bool,
  enableRuneWords: PropTypes.bool,
  enableDominationShards: PropTypes.bool,
  simOptionWarnings: PropTypes.array,

  onSubmit: PropTypes.func.isRequired,
  onTogglePrecision: PropTypes.func.isRequired,
  onClickClearApl: PropTypes.func.isRequired,
};

SubmitSimulationButton.defaultProps = {
  loading: false,
  characterLoading: false,
  itemsLoading: false,
  optimizeLoading: false,
  error: null,
  ptr: false,
  isExpertActive: false,
  inputInCustomAplWarning: false,
  enableRuneWords: false,
  enableDominationShards: false,
  simOptionWarnings: [],
};

const mapStateToProps = (state, props) => ({
  isLoggedIn: userSelectors.isLoggedIn(state, props),
  formData: simbotSelectors.formData(state, props),
  iterations: simcOptionsSelectors.iterations(state, props),
  smartHighPrecision: simcOptionsSelectors.smartHighPrecision(state, props),
  characterLoading: characterSelectors.loading(state, props),
  ptr: simcOptionsSelectors.ptr(state, props),
  isExpertActive: simcExpertSelectors.isExpertActive(state, props),
  inputInCustomAplWarning: simbotSelectors.inputInCustomAplWarning(state, props),
  className: simbotSelectors.characterClassName(state, props),
  spec: simbotSelectors.characterSpec(state, props),
  enableRuneWords: simcOptionsSelectors.enableRuneWords(state, props),
  enableDominationShards: simcOptionsSelectors.enableDominationShards(state, props),
  simOptionWarnings: simbotSelectors.simOptionWarnings(state, props),
  openSimInNewTab: formSelectors.openSimInNewTab(state, props),

  loading: formSelectors.loading(state, props),
  error: formSelectors.error(state, props),
  itemsLoading: simcItemsSelectors.loading(state, props),
  optimizeLoading: optimizeSelectors.loading(state, props),
});

const mapDispatchToProps = dispatch => ({ dispatch });

const mergeProps = (state, { dispatch }, props) => ({
  ...props,
  ...state,
  onTogglePrecision: () => dispatch(actions.toggleSmartHighPrecision()),
  onClickClearApl: () => {
    return dispatch(actions.clearCustomApl(
      state.className,
      state.spec,
    ));
  },
  onSubmit: (e) => {
    // flip this bit to enable reporting any missing error messages to sentry, but just once
    shouldReportError = true;

    e.preventDefault();

    // button 1 is middle click
    const openInNewTab = state.openSimInNewTab || e.ctrlKey || e.button === 1;

    dispatch(actions.submitForm(state.formData, state.isLoggedIn)).then((data) => {
      const url = `/simbot/report/${data.simId}`;
      if (openInNewTab) {
        const newTab = window.open(url, '_blank');
        if (newTab === null) {
          dispatch(actions.formError('submit_new_tab_blocked'));
        }
      } else {
        props.router.push(url);
      }
    }).catch(() => {
      // do nothing, error handling dispatches are already good to go
    });
  },
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps, mergeProps)(SubmitSimulationButton),
);
